import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

const DEFAULT_VISIBILITY_STATE: DocumentVisibilityState = "visible";
type VisibilityChangeCallback = (visibilityState: DocumentVisibilityState) => void;

function getVisibilitySnapshot(): DocumentVisibilityState {
  if (typeof document === "undefined") {
    return DEFAULT_VISIBILITY_STATE;
  }

  return document.visibilityState;
}

export function useVisibility(
  onVisibilityChange?: VisibilityChangeCallback
): DocumentVisibilityState {
  const onVisibilityChangeRef = useRef(onVisibilityChange);

  useEffect(() => {
    onVisibilityChangeRef.current = onVisibilityChange;
  }, [onVisibilityChange]);

  const subscribeToVisibilityChange = useCallback((onStoreChange: () => void): (() => void) => {
    if (typeof document === "undefined") {
      return () => undefined;
    }

    const handleVisibilityChange = () => {
      const visibilityState = getVisibilitySnapshot();
      onVisibilityChangeRef.current?.(visibilityState);
      onStoreChange();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return useSyncExternalStore(
    subscribeToVisibilityChange,
    getVisibilitySnapshot,
    () => DEFAULT_VISIBILITY_STATE
  );
}
